/* eslint-disable @typescript-eslint/restrict-template-expressions */
import type {
  AbilityRecord,
  Bootstrap,
  CompiledManifest,
  Session,
} from "./generated/contracts";
import type { components } from "./generated/openapi";
import { isHttpsUrl } from "../host/adapter";
import { logger } from "../logging/logger";

type CreateReminderWire = Omit<
  components["schemas"]["CreateReminderBody"],
  "interval"
> & {
  interval?: number;
};
type UpdateReminderWire = Omit<
  components["schemas"]["UpdateReminderBody"],
  "interval"
> & {
  interval?: number;
};

export interface ProposalResponse {
  id: string;
  status: "pending" | "applied" | "rejected" | "stale" | "expired";
  summary: { operation: string; record_id?: string | null }[];
}

export interface AssistantJobResponse extends Omit<
  components["schemas"]["AssistantJobResponse"],
  "state"
> {
  state:
    | "accepted"
    | "queued"
    | "running"
    | "waiting"
    | "succeeded"
    | "failed"
    | "cancel_requested"
    | "cancelled"
    | "expired";
}
export type FeedbackReason =
  | "wrong_action"
  | "claimed_but_not_done"
  | "wrong_data"
  | "wrong_language"
  | "too_slow"
  | "other";

/** `share_for_review` is present only when the owner ticked the consent box. */
export interface FeedbackSubmission {
  verdict: "up" | "down";
  reason?: FeedbackReason;
  share_for_review?: true;
}

export type FeedbackResponse = components["schemas"]["FeedbackResponse"];

export interface ApiError {
  status: number;
  code: string;
  message: string;
  correlationId?: string;
  /** Why a schema cannot be published, when the server names the parts. */
  diagnostics?: SchemaDiagnostic[];
  /** When a refused request may be retried (ISO instant), if the server says. */
  retryAfter?: string;
}

/** Account lifecycle shapes, straight from the published contract. */
export type ClearCategory = components["schemas"]["ClearCategory"];
export type AccountStatus = components["schemas"]["AccountStatusResponse"];
export type ClearingPreview = components["schemas"]["ClearingPreviewResponse"];
export type ExportItem = components["schemas"]["ExportItem"];
export type DeletionResult = components["schemas"]["DeletionResponse"];

export interface SchemaDiagnostic {
  code: string;
  severity: "error" | "warning";
  path: string;
  message: string;
}

export interface ApiClient {
  reminders: () => Promise<ReminderResponse[]>;
  setTimezone: (timezone: string) => Promise<{ timezone: string }>;
  setLocale: (userId: string, locale: string) => Promise<{ locale: string }>;
  createReminder: (body: CreateReminderWire) => Promise<ReminderResponse>;
  updateReminder: (
    id: string,
    body: UpdateReminderWire,
  ) => Promise<ReminderResponse>;
  transitionReminder: (
    id: string,
    revision: number,
    operation: "complete" | "resume" | "remove",
  ) => Promise<unknown>;
  deleteRecord: (
    scope: { workspaceId: string; abilityId: string; actorId: string },
    current: AbilityRecord,
    key: string,
  ) => Promise<void>;
  record: (
    workspaceId: string,
    abilityId: string,
    recordId: string,
  ) => Promise<AbilityRecord>;
  saveRecord: (
    scope: {
      workspaceId: string;
      abilityId: string;
      actorId: string;
      schemaVersionId: string;
    },
    collectionId: string,
    payload: Record<string, unknown>,
    key: string,
    current?: AbilityRecord,
  ) => Promise<AbilityRecord>;
  profile: (userId: string) => Promise<{
    plan_id: string | null;
    subscription_state: string;
    billing_enabled: boolean;
    usage: Record<string, number>;
    packs: { tier: string; remaining_credits: number }[];
    orders: { product_id: string; state: string }[];
    is_operator?: boolean;
    locale?: string | null;
    timezone?: string | null;
  }>;
  bootstrap: () => Promise<Bootstrap>;
  records: (
    workspaceId: string,
    abilityId: string,
    collectionId: string,
    offset?: number,
  ) => Promise<AbilityRecord[]>;
  preview: (abilityId: string) => Promise<DraftPreview>;
  publish: (
    abilityId: string,
    actorId: string,
    draftRevision: number,
    /** `rollback` marks a Draft restored from a prior version (ADR 0037). */
    reason?: "user_confirmed" | "rollback",
  ) => Promise<PublishResult>;
  /** Published versions, newest first (at most 50). */
  listVersions: (abilityId: string) => Promise<SchemaVersion[]>;
  /**
   * Open a Draft that restores a prior version; refused with `draft_exists`,
   * `version_not_restorable` or `draft_has_no_changes` (409).
   */
  draftFromVersion: (
    abilityId: string,
    versionId: string,
  ) => Promise<{ id: string; revision: number }>;
  /** The keywords of a section as the owner sees them (ADR 0236). */
  routingProfile: (abilityId: string) => Promise<RoutingProfileView>;
  /**
   * Edit the owner's keywords; the change lands in the Draft, which is
   * reviewed and published like any other (`draft_*` 409 when it cannot).
   */
  editRoutingTerms: (
    abilityId: string,
    changes: RoutingTermChange[],
    language: "ru" | "en",
  ) => Promise<RoutingEditResult>;
  /**
   * Discard the schema draft; a 404 means there is none, a 409 that it moved
   * past `expectedRevision` (owner only).
   */
  deleteDraft: (abilityId: string, expectedRevision?: number) => Promise<void>;
  renameAbility: (
    abilityId: string,
    name: string,
  ) => Promise<{ ability_id: string; name: string }>;
  redeemNavigation: (token: string, actorId: string) => Promise<string>;
  proposal: (proposalId: string, actorId: string) => Promise<ProposalResponse>;
  assistantJob: (
    jobId: string,
    actorId: string,
  ) => Promise<AssistantJobResponse>;
  cancelAssistantJob: (
    jobId: string,
    actorId: string,
  ) => Promise<AssistantJobResponse>;
  retryAssistantJob: (
    jobId: string,
    actorId: string,
  ) => Promise<AssistantJobResponse>;
  submitJobFeedback: (
    jobId: string,
    actorId: string,
    body: FeedbackSubmission,
  ) => Promise<FeedbackResponse>;
  confirmProposal: (
    proposalId: string,
    actorId: string,
  ) => Promise<ProposalResponse>;
  rejectProposal: (
    proposalId: string,
    actorId: string,
  ) => Promise<ProposalResponse>;
  /** Open a Pro order and return the invoice link to hand to Telegram. */
  startProCheckout: (idempotencyKey: string) => Promise<string>;
  /** Enabled payment channels and the catalog with per-channel prices. */
  billingOptions: () => Promise<BillingOptions>;
  /** Open a checkout on the SBP channel; the order id feeds the status poll. */
  startSbpCheckout: (
    idempotencyKey: string,
    productId: string,
  ) => Promise<SbpCheckout>;
  /** State of the caller's own order, for the post-payment poll. */
  billingOrder: (orderId: string) => Promise<BillingOrderStatus>;
  /** Lifecycle of the workspace; allowed for a restricted session. */
  accountStatus: () => Promise<AccountStatus>;
  /** Ask for a data export; the link is delivered in the bot, never here. */
  requestExport: () => Promise<{ id: string }>;
  /** Active export links without their tokens. */
  listExports: () => Promise<ExportItem[]>;
  revokeExport: (id: string) => Promise<void>;
  revokeAllExports: () => Promise<{ revoked: number }>;
  /** Rows each category holds now, i.e. what clearing would remove. */
  clearingPreview: () => Promise<ClearingPreview>;
  clearData: (
    categories: ClearCategory[],
    confirmation: string,
  ) => Promise<{ cleared: Record<string, number> }>;
  requestDeletion: (confirmation: string) => Promise<DeletionResult>;
  /** Restore a workspace inside the cancellation window. */
  cancelDeletion: () => Promise<{ restored: boolean }>;
}

export interface SbpCheckout {
  checkoutUrl: string;
  /** Absent on a backend that predates the order poll. */
  orderId: string | null;
}

export type BillingOrderState = "pending" | "paid" | "expired" | "failed";

export type BillingOrderStatus = components["schemas"]["BillingOrderResponse"];

type InvoiceCheckout = components["schemas"]["InvoiceCheckoutResponse"];
type SbpCheckoutResponse = components["schemas"]["SbpCheckoutResponse"];

export type BillingChannel = "stars" | "sbp";

export type BillingPrice = components["schemas"]["BillingPriceView"];
export type BillingProduct = components["schemas"]["BillingProductView"];
export type BillingOptions = components["schemas"]["BillingOptionsResponse"];

/** One entry of the server-computed change list (ADR 0201). Labels only. */
export interface SchemaChange {
  kind: string;
  path: string;
  collection_id?: string | null;
  field_id?: string | null;
  option_id?: string | null;
  view_id?: string | null;
  section_id?: string | null;
  element_id?: string | null;
  control_id?: string | null;
  print_form_id?: string | null;
  before?: string | null;
  after?: string | null;
}

interface BackfillPlan {
  collection_id: string;
  field_id: string;
  default: unknown;
  /** Existing records the publish would fill with `default`. */
  records: number;
}

export interface DraftCompatibility {
  classification: "compatible" | "requires_data_migration" | "unsupported";
  backfill: BackfillPlan[];
  /** Operations a publish would refuse, by code and path. */
  diagnostics: { code: string; path: string; message: string }[];
}

export interface SchemaVersion {
  id: string;
  version_number: number;
  published_at: string;
  active: boolean;
}

/** One keyword of a section's routing profile (ADR 0236). */
export interface RoutingTerm {
  term: string;
  /** Built from the owner's labels, suggested by the assistant or added by the owner. */
  source: "labels" | "suggested" | "owner";
  /** The owner switched this term off. */
  removed: boolean;
}

interface RoutingTarget {
  /** `ability`, `collection:<id>`, `field:<collection>.<id>` or `view:<id>`. */
  key: string;
  kind: string;
  label: string;
  terms: RoutingTerm[];
}

export interface RoutingProfileView {
  origin: string;
  stale: boolean;
  targets: RoutingTarget[];
  phrases: RoutingTerm[];
  /** The terms shown are those of the pending Draft. */
  draft_pending: boolean;
  draft_revision: number | null;
}

export interface RoutingTermChange {
  op: "add_routing_term" | "remove_routing_term";
  /** Omitted for a typical phrase. */
  target?: string;
  term: string;
}

export interface RoutingEditResult {
  saved: boolean;
  draft_revision: number | null;
  refused: { op: string; code: string; text: string }[];
}

interface PublishResult {
  versionNumber: number | null;
  /** Existing records that received a default value (AGE-006). */
  backfilledRecords: number;
}

export interface DraftPreview {
  manifest: CompiledManifest;
  draftRevision: number;
  baseVersionId?: string | null;
  /** Absent on servers older than AGE-007. */
  changes?: SchemaChange[];
  compatibility?: DraftCompatibility | null;
  baseVersionNumber?: number | null;
  stale?: boolean;
}

interface PreviewResponse {
  valid: boolean;
  manifest: CompiledManifest | null;
  diagnostics: Record<string, unknown>[];
  revision: number;
  base_version_id?: string | null;
  base_version_number?: number | null;
  stale?: boolean;
  changes?: SchemaChange[];
  compatibility?: DraftCompatibility | null;
}

export interface ReminderResponse {
  reminder_id: string;
  title: string;
  timezone: string;
  local_time: string;
  next_occurrence_at: string;
  recurrence: "once" | "daily" | "weekly" | "monthly";
  revision: number;
  state: "active" | "completed" | "cancelled";
  interval: number;
  /** The bot failed to send the current occurrence; it stays active. */
  missed?: boolean;
}

const SAFE_INTERNAL_ROUTE = /^\/(?![/\\])/;

/** True for same-origin absolute paths; rejects `//host` and `/\host`. */
export function isSafeInternalRoute(route: unknown): route is string {
  return typeof route === "string" && SAFE_INTERNAL_ROUTE.test(route);
}

/** The support reference carried by a failed API call, if the server sent one. */
export function correlationIdOf(cause: unknown): string | undefined {
  if (!(cause instanceof Error)) return undefined;
  const detail = cause.cause as Partial<ApiError> | undefined;
  return typeof detail?.correlationId === "string" && detail.correlationId
    ? detail.correlationId
    : undefined;
}

/** The stable status and code of a failed API call; undefined for other errors. */
export function apiErrorOf(
  cause: unknown,
): Pick<ApiError, "status" | "code" | "retryAfter"> | undefined {
  if (!(cause instanceof Error)) return undefined;
  const detail = cause.cause as Partial<ApiError> | undefined;
  if (typeof detail?.status !== "number" || typeof detail.code !== "string")
    return undefined;
  return {
    status: detail.status,
    code: detail.code,
    retryAfter: detail.retryAfter,
  };
}

const OPAQUE_SEGMENT = /^(?:[0-9a-f]{8}-[0-9a-f-]{27}|\d+|[0-9a-z_-]{20,})$/i;

/** Path without query string; opaque id segments collapse to `:id`. */
export function routeTemplate(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] ?? "";
  return pathname
    .split("/")
    .map((segment) => (OPAQUE_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");
}

export function createApiClient(fetcher: typeof fetch = fetch): ApiClient {
  const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const headers = new Headers(init?.headers);
    if (init?.body !== undefined && init.body !== null)
      headers.set("Content-Type", "application/json");
    if (init?.method && !["GET", "HEAD"].includes(init.method)) {
      const csrf = cookieValue("mioscape_csrf");
      if (csrf) headers.set("X-CSRF-Token", csrf);
    }
    const startedAt = performance.now();
    const response = await fetcher(`/api/v1${path}`, {
      ...init,
      credentials: "same-origin",
      headers,
    });
    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => ({}));
      const body =
        payload !== null &&
        typeof payload === "object" &&
        !Array.isArray(payload)
          ? (payload as Record<string, unknown>)
          : {};
      const detail = typeof body.detail === "string" ? body.detail : undefined;
      const code =
        typeof body.code === "string" && body.code.length > 0
          ? body.code
          : (detail ?? "request_failed");
      const message =
        typeof body.message === "string" && body.message.length > 0
          ? body.message
          : (detail ?? `Request failed (${response.status})`);
      const correlationId =
        response.headers.get("X-Correlation-ID") ?? undefined;
      logger.error("api_request_failed", {
        method: init?.method ?? "GET",
        route: routeTemplate(path),
        status: response.status,
        code,
        duration_ms: Math.round(performance.now() - startedAt),
        correlationId,
      });
      throw new Error(message, {
        cause: {
          status: response.status,
          code,
          correlationId,
          retryAfter:
            typeof body.retry_after === "string" ? body.retry_after : undefined,
          diagnostics: Array.isArray(body.diagnostics)
            ? (body.diagnostics as SchemaDiagnostic[])
            : undefined,
        } satisfies Omit<ApiError, "message">,
      });
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  };

  return {
    reminders: () => request<ReminderResponse[]>("/reminders"),
    setLocale: (userId, locale) =>
      request<{ locale: string }>(
        `/profile/locale?user_id=${encodeURIComponent(userId)}`,
        { method: "PUT", body: JSON.stringify({ locale }) },
      ),
    setTimezone: (timezone) =>
      request<{ timezone: string }>("/reminders/timezone", {
        method: "PUT",
        body: JSON.stringify({ timezone }),
      }),
    createReminder: (body) =>
      request<ReminderResponse>("/reminders", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateReminder: (id, body) =>
      request<ReminderResponse>(`/reminders/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    transitionReminder: (id, revision, operation) =>
      request(`/reminders/${encodeURIComponent(id)}/${operation}`, {
        method: "POST",
        body: JSON.stringify({ revision, confirmed: true }),
      }),
    deleteRecord: (scope, current, key) =>
      request(
        `/ability-records/${encodeURIComponent(scope.workspaceId)}/${encodeURIComponent(scope.abilityId)}/${encodeURIComponent(current.id)}?${new URLSearchParams({ actor_id: scope.actorId, expected_revision: String(current.revision) }).toString()}`,
        {
          method: "DELETE",
          body: JSON.stringify({ confirmed: true, idempotency_key: key }),
        },
      ).then(() => undefined),
    record: (workspaceId, abilityId, recordId) =>
      request<AbilityRecord>(
        `/ability-records/${encodeURIComponent(workspaceId)}/${encodeURIComponent(abilityId)}/${encodeURIComponent(recordId)}`,
      ),
    saveRecord: (scope, collectionId, payload, key, current) => {
      const query = new URLSearchParams({ actor_id: scope.actorId });
      if (current) query.set("expected_revision", String(current.revision));
      return request<AbilityRecord>(
        `/ability-records/${encodeURIComponent(scope.workspaceId)}/${encodeURIComponent(scope.abilityId)}/${encodeURIComponent(current?.id ?? collectionId)}?${query.toString()}`,
        {
          method: current ? "PATCH" : "POST",
          body: JSON.stringify({ payload, idempotency_key: key }),
        },
      );
    },
    profile: (userId) =>
      request(`/profile?user_id=${encodeURIComponent(userId)}`),
    bootstrap: () => request<Bootstrap>("/bootstrap"),
    records: (workspaceId, abilityId, collectionId, offset = 0) =>
      request<AbilityRecord[]>(
        `/ability-records/${encodeURIComponent(workspaceId)}/${encodeURIComponent(abilityId)}/collections/${encodeURIComponent(collectionId)}?offset=${offset}`,
      ),
    preview: async (abilityId) => {
      const response = await request<PreviewResponse>(
        `/ability-schema/${encodeURIComponent(abilityId)}/preview`,
        { method: "POST" },
      );
      if (!response.valid || response.manifest === null)
        throw new Error("The Draft cannot be previewed.");
      return {
        manifest: response.manifest,
        draftRevision: response.revision,
        baseVersionId: response.base_version_id,
        changes: response.changes ?? [],
        compatibility: response.compatibility ?? null,
        baseVersionNumber: response.base_version_number ?? null,
        stale: response.stale ?? false,
      };
    },
    publish: (abilityId, actorId, draftRevision, reason = "user_confirmed") =>
      request(`/ability-schema/${encodeURIComponent(abilityId)}/publish`, {
        method: "POST",
        body: JSON.stringify({
          actor_id: actorId,
          draft_revision: draftRevision,
          confirmed: true,
          reason,
        }),
      }).then((published) => {
        const body = published as {
          version_number?: number;
          backfilled_records?: number;
        } | null;
        return {
          versionNumber: body?.version_number ?? null,
          backfilledRecords: body?.backfilled_records ?? 0,
        };
      }),
    listVersions: (abilityId) =>
      request<SchemaVersion[]>(
        `/ability-schema/${encodeURIComponent(abilityId)}/versions`,
      ),
    draftFromVersion: (abilityId, versionId) =>
      request<{ id: string; revision: number }>(
        `/ability-schema/${encodeURIComponent(abilityId)}/versions/${encodeURIComponent(versionId)}/draft`,
        { method: "POST" },
      ),
    routingProfile: (abilityId) =>
      request<RoutingProfileView>(
        `/ability-schema/${encodeURIComponent(abilityId)}/routing`,
      ),
    editRoutingTerms: (abilityId, changes, language) =>
      request<RoutingEditResult>(
        `/ability-schema/${encodeURIComponent(abilityId)}/routing/terms`,
        { method: "POST", body: JSON.stringify({ changes, language }) },
      ),
    deleteDraft: (abilityId, expectedRevision) =>
      request(
        `/ability-schema/${encodeURIComponent(abilityId)}/draft${
          expectedRevision === undefined
            ? ""
            : `?expected_revision=${String(expectedRevision)}`
        }`,
        { method: "DELETE" },
      ).then(() => undefined),
    renameAbility: (abilityId, name) =>
      request<{ ability_id: string; name: string }>(
        `/abilities/${encodeURIComponent(abilityId)}/rename`,
        { method: "POST", body: JSON.stringify({ name }) },
      ),
    assistantJob: (jobId, actorId) =>
      request<AssistantJobResponse>(
        `/assistant/jobs/${encodeURIComponent(jobId)}?actor_id=${encodeURIComponent(actorId)}`,
      ),
    cancelAssistantJob: (jobId, actorId) =>
      request<AssistantJobResponse>(
        `/assistant/jobs/${encodeURIComponent(jobId)}/cancel?actor_id=${encodeURIComponent(actorId)}`,
        { method: "POST" },
      ),
    retryAssistantJob: (jobId, actorId) =>
      request<AssistantJobResponse>(
        `/assistant/jobs/${encodeURIComponent(jobId)}/retry?actor_id=${encodeURIComponent(actorId)}`,
        { method: "POST" },
      ),
    submitJobFeedback: (jobId, actorId, body) =>
      request<FeedbackResponse>(
        `/assistant/jobs/${encodeURIComponent(jobId)}/feedback?actor_id=${encodeURIComponent(actorId)}`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    proposal: (proposalId, actorId) =>
      request<ProposalResponse>(
        `/proposals/${encodeURIComponent(proposalId)}?actor_id=${encodeURIComponent(actorId)}`,
      ),
    confirmProposal: (proposalId, actorId) =>
      request<ProposalResponse>(
        `/proposals/${encodeURIComponent(proposalId)}/confirm?actor_id=${encodeURIComponent(actorId)}`,
        { method: "POST" },
      ),
    rejectProposal: (proposalId, actorId) =>
      request<ProposalResponse>(
        `/proposals/${encodeURIComponent(proposalId)}/reject?actor_id=${encodeURIComponent(actorId)}`,
        { method: "POST" },
      ),
    startProCheckout: async (idempotencyKey) => {
      const response = await request<InvoiceCheckout>("/billing/checkout", {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
      });
      // The link goes straight to Telegram's own sheet, so anything that is
      // not a Telegram link is refused rather than opened.
      if (!response.invoice_url.startsWith("https://t.me/"))
        throw new Error("The invoice link is invalid.");
      return response.invoice_url;
    },
    billingOptions: () => request<BillingOptions>("/billing/options"),
    startSbpCheckout: async (idempotencyKey, productId) => {
      const response = await request<SbpCheckoutResponse>("/billing/checkout", {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify({ product_id: productId, channel: "sbp" }),
      });
      // The provider page is opened outside the app, so only https leaves.
      if (!isHttpsUrl(response.checkout_url))
        throw new Error("The checkout link is invalid.");
      return {
        checkoutUrl: response.checkout_url,
        orderId:
          typeof response.order_id === "string" ? response.order_id : null,
      };
    },
    billingOrder: (orderId) =>
      request<BillingOrderStatus>(
        `/billing/orders/${encodeURIComponent(orderId)}`,
      ),
    accountStatus: () => request<AccountStatus>("/account/status"),
    requestExport: () =>
      request<{ id: string }>("/account/export", { method: "POST" }),
    listExports: async () =>
      (await request<{ exports?: ExportItem[] }>("/account/exports")).exports ??
      [],
    revokeExport: (id) =>
      request(`/account/exports/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }).then(() => undefined),
    revokeAllExports: () =>
      request<{ revoked: number }>("/account/exports", { method: "DELETE" }),
    clearingPreview: () => request<ClearingPreview>("/account/data-clearing"),
    clearData: (categories, confirmation) =>
      request<{ cleared: Record<string, number> }>("/account/data-clearing", {
        method: "POST",
        body: JSON.stringify({ categories, confirmation }),
      }),
    requestDeletion: (confirmation) =>
      request<DeletionResult>("/account/deletion", {
        method: "POST",
        body: JSON.stringify({ confirmation }),
      }),
    cancelDeletion: () =>
      request<{ restored: boolean }>("/account/deletion/cancel", {
        method: "POST",
      }),
    redeemNavigation: async (token, actorId) => {
      const params = new URLSearchParams({ token, actor_id: actorId });
      const response = await request<{ route: string }>(
        `/navigation/redeem?${params.toString()}`,
        { method: "POST" },
      );
      if (!isSafeInternalRoute(response.route))
        throw new Error("The navigation target is invalid.");
      return response.route;
    },
  };
}

function cookieValue(name: string): string | undefined {
  const prefix = `${encodeURIComponent(name)}=`;
  const value = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));
  return value === undefined
    ? undefined
    : decodeURIComponent(value.slice(prefix.length));
}

export type { Session };
